import {
  Component,
  afterNextRender,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { IconComponent } from '@app/components/ui-icon/icon.component';
import { FormatCurrencyPipe } from '@app/pipes/format-currency.pipe';
import { SeoRoutePipe } from '@app/pipes/seo-route.pipe';
import { TranslatePipe } from '@app/pipes/translate.pipe';
import { PosthogService } from '@app/services/logic/posthog.service';
import { LocalizationService } from '@services/logic/localization.service';
import { minorToMajor } from '@utils/minor-unit';

export const DAMAGE_COMPENSATION_LINE_FLAG = 'damage-compensation-trust-line';

/**
 * A/B test (PostHog flag `damage-compensation-trust-line`): a short line under
 * the hero CTA on the homepage and the /hh pages, linking to the damage
 * compensation page.
 *
 * DK only — other markets never read the flag, so they record no exposure.
 * Hidden on the server and on first client render (flags resolve in the
 * browser); shown only for the literal 'test' variant. The flag is read in
 * both arms, so the control arm records exposure too.
 */
@Component({
  selector: 'app-damage-compensation-line',
  standalone: true,
  imports: [IconComponent, SeoRoutePipe, TranslatePipe, FormatCurrencyPipe],
  templateUrl: './damage-compensation-line.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./damage-compensation-line.component.scss'],
})
export class DamageCompensationLineComponent {
  private readonly posthog = inject(PosthogService);
  private readonly localizationService = inject(LocalizationService);

  protected readonly show = signal<boolean>(false);

  /** Damage compensation cap in major units. */
  protected readonly compensationMaximum = computed(() =>
    minorToMajor(this.localizationService.compensationMaximum()),
  );

  constructor() {
    afterNextRender(() => {
      this.posthog.onFeatureFlags(() => {
        // Multivariate experiment flag: check the variant explicitly.
        // `isFeatureEnabled` is truthy for 'control' too.
        const enabled =
          this.posthog.getFeatureFlag(DAMAGE_COMPENSATION_LINE_FLAG) === 'test';

        this.show.set(enabled);
      });
    });
  }
}
